import { type DateRangePresetLabel, type Period, presetPeriod } from '@prerender/design-system/DateRangePicker';
import { readDomainParam } from '~components/Filters/domainParam';

export const DISCOVERABILITY_DEFAULT_PRESET: DateRangePresetLabel = 'last 30 days';
export const DISCOVERABILITY_DEFAULT_PERIOD: Period = presetPeriod(DISCOVERABILITY_DEFAULT_PRESET);

export const QueryParamNames = {
  DateFrom: 'dateFrom',
  DateTo: 'dateTo',
  Crawlers: 'crawlers',
  Domain: 'domain',
} as const;

type QueryParamName = typeof QueryParamNames[keyof typeof QueryParamNames];

const QueryParamNamesList = Object.values(QueryParamNames);

export type DiscoverabilityQueryParams = Partial<{
  dateFrom: Date;
  dateTo: Date;
  crawlers: string[];
  domain: string;
  // Write-only scan-scope clear signals — the read side is useScanScopeParams;
  // writing values through this type is a bug. Not part of QueryParamNames /
  // isDiscoverabilityParam: the read side never parses these back. They exist
  // so a filter change can pass them as `undefined` to updateMultipleParameters
  // and drop them from the URL, exiting scan scope. Typed as `undefined`-only so
  // the only legal write is a clear.
  readonly dateTimeFrom?: undefined;
  readonly dateTimeTo?: undefined;
  readonly domains?: undefined;
}>;

export const CLEARED_SCAN_SCOPE = {
  dateTimeFrom: undefined,
  dateTimeTo: undefined,
  domains: undefined,
} as const;

export function isDiscoverabilityParam(name: string): name is QueryParamName {
  return QueryParamNamesList.includes(name as QueryParamName);
}

export function buildDiscoverabilityQueryParameters(
  data: Record<string, string>
): DiscoverabilityQueryParams {
  return Object.entries(data).reduce<DiscoverabilityQueryParams>((acc, [name, value]) => {
    if (!isDiscoverabilityParam(name)) return acc;

    if (name === QueryParamNames.DateFrom || name === QueryParamNames.DateTo) {
      const parsed = new Date(`${value}T00:00`);
      if (!Number.isNaN(parsed.getTime())) acc[name] = parsed;
    } else if (name === QueryParamNames.Crawlers) {
      acc[name] = value.split(',');
    } else if (name === QueryParamNames.Domain) {
      acc[name] = readDomainParam(value);
    }

    return acc;
  }, {});
}
